import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';

import bi from 'shared/bi/bi';

import WixSDK from 'shared/utils/wix-sdk';
import fedopsLogger from 'widget/utils/fedops-logger';
import memoizedPartial from 'shared/utils/memoized-partial';
import { openMobileOverlay } from 'shared/utils/open-overlay';
import { playerSizeMobileMainItemForNewUX } from 'widget/utils/videos-sizes/videos-sizes';
import { storeForReload } from 'widget/utils/reload';
import { sendPerformanceInfo, addSingleMeasurement } from 'shared/utils/performance';
import performanceTypes from 'shared/utils/performance/types';
import { getOpenedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import { withBiEvent } from 'shared/bi/send-bi-event';
import { notForPreview } from 'widget/utils/not-for-preview';
import { convertSToMS } from '@wix/wix-vod-shared/src/widget/utils/time';
import { canPlayFullOrPreview } from '@wix/wix-vod-shared/src/widget/ui-selectors/video-access';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { getMainVideoId } from '@wix/wix-vod-shared/src/widget/ui-selectors/selected-video-id';
import { isVideoPlayingOptimistic } from 'widget/selectors/video-playback-status';
import { getChannelForWidget } from '@wix/wix-vod-shared/src/common/selectors/channels';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import { canPlayVideoInFrame } from './ui-selectors/can-play-video-in-frame';
import { updateCSS } from 'shared/utils/update-css';
import { requestLogin } from 'shared/utils/auth';

import { openVideoPage } from 'widget/redux/client/actions/mobile-overlay-openners/main-openners';
import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, pauseVideo } from 'widget/redux/client/actions/player/change-playback-status';

import { MOBILE_WIDGET_DATA_KEY } from 'widget/data-components/lazy-channel-videos';
import { MAIN_ITEM_TYPE_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/main-item-type-values';
import DeeplinkPopups from 'widget/components/deeplink-popups/deeplink-popups';
import MainWidgetMobilePlayer from 'widget/components/players/main-widget-mobile-player';
import SlidableListWithLazyLoad from './components/slidable-list-with-lazy-load';
import CoverWithOverlay from './components/cover-with-overlay/cover-with-overlay';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import NoVideosContent from 'widget/components/no-content/no-videos-content/no-videos-content';
import ResizedImage from 'shared/components/resized-image/resized-image';

import styles from './home.styl';


@connect(state => ({
  isVideoPlaying: isVideoPlayingOptimistic(state),
  canShowChannelCover: !state.isVideosTouched,
  appSettings: getAllSettings(state),
  videoByIds: getVideosGroupedByIds(state),
  videoIds: state.lazyVideoLists[MOBILE_WIDGET_DATA_KEY].videoIds,
  channelData: getChannelForWidget(state),
  currentSiteUser: getCurrentSiteUser(state),
  mainVideoId: getMainVideoId(state),
  canPlayVideoInFrame: canPlayVideoInFrame(state, MOBILE_WIDGET_DATA_KEY),
}), {
  openVideoPage,
  selectVideo,
  resetSelectedVideo,
  playVideo,
  pauseVideo,
})
export default class HomeView extends React.Component {

  static displayName = 'MobileView';

  static propTypes = {
    appSettings: PropTypes.object.isRequired,
    windowSize: PropTypes.object.isRequired,
    channelData: PropTypes.object.isRequired,
    selectVideo: PropTypes.func.isRequired,
    canShowChannelCover: PropTypes.bool.isRequired,
    isVideoPlaying: PropTypes.bool.isRequired,
    resetSelectedVideo: PropTypes.func.isRequired,
    mainVideoId: PropTypes.string,
    videoByIds: PropTypes.object.isRequired,
    currentSiteUser: PropTypes.object,
    playVideo: PropTypes.func.isRequired,
    videoIds: PropTypes.array,
    isCurrentSiteUserFetching: PropTypes.bool,
    isPaymentInfoFetching: PropTypes.bool,
    openVideoPage: PropTypes.func,
    canPlayVideoInFrame: PropTypes.bool,
  };

  componentDidMount() {
    const { appSettings, channelData, canShowChannelCover } = this.props;

    updateCSS(appSettings);

    addSingleMeasurement(performanceTypes.appFullyRendered);
    sendPerformanceInfo();
    WixSDK.Performance.applicationLoaded();

    bi.log(
      'widget.opened',
      getOpenedEventParams(appSettings, channelData, this.mainVideo, canShowChannelCover),
    );
    fedopsLogger.appLoaded();
  }

  get playerSize() {
    const { width } = this.props.windowSize;
    return playerSizeMobileMainItemForNewUX(width);
  }

  @autobind
  openMemberOnlyPage(id) {
    bi.logWidgetSystem('video.memberOnlyInfo.mobileOverlay.requested');
    openMobileOverlay(`#/membership/${id}?shouldCloseMobileOverlay=true`);
  }

  @autobind
  processDataFromStorage({ selectedVideoId }) {
    if (selectedVideoId) {
      this.props.selectVideo(selectedVideoId);
    }
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      selectedVideoId: this.props.mainVideoId,
    });
  }

  get mainVideo() {
    const { mainVideoId, videoByIds } = this.props;
    return videoByIds[mainVideoId];
  }

  @autobind
  playSelectedVideo() {
    const { channelData, currentSiteUser, selectVideo, playVideo } = this.props;

    const videoItem = this.mainVideo;
    const { videoSource } = videoItem;
    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser);

    if (isPlayAllowed) {
      bi.log('widget.vid.clicked', {
        duration: convertSToMS(videoItem.durationSec),
        channelID: channelData.id,
        videoType: videoSource,
      });

      playVideo(videoItem.id);
    } else {
      selectVideo(videoItem.id);
    }
  }

  @autobind
  onCurrentSlideChanged(newIndex) {
    const { videoIds, selectVideo } = this.props;
    selectVideo(videoIds[newIndex]);
  }

  get noVideoPlayerOverlay() {
    const { width, height } = this.playerSize;

    return (
      <CoverWithOverlay channelData={this.props.channelData}
                        showChannelCover
                        videoItem={{}}
                        itemWidth={width}
                        itemHeight={height} />
    );
  }

  @autobind
  onCoverPlayClick() {
    // TODO: move to action
    const { canPlayVideoInFrame, openVideoPage, mainVideoId } = this.props;
    if (canPlayVideoInFrame) {
      this.playSelectedVideo();
    } else {
      openVideoPage(mainVideoId);
    }
  }

  @autobind
  @withBiEvent('widget.signIn.clicked')
  @notForPreview
  showLogInForm() {
    requestLogin()
      .then(this.storeDataForReload);
  }

  renderCoverForCarousel(videoItem, index, className = null) {
    const { canShowChannelCover, channelData } = this.props;

    const { width, height } = this.playerSize;
    const featuredVideoId = _.get(channelData, 'featuredItem');
    const isInitialVideo = featuredVideoId ? featuredVideoId === videoItem.id : index === 0;
    const showChannelCover = canShowChannelCover && isInitialVideo;

    return (
      <CoverWithOverlay videoItem={videoItem}
                        className={className}
                        showChannelCover={showChannelCover}
                        itemWidth={width}
                        itemHeight={height}
                        onSignInRequested={this.showLogInForm}
                        onPlayClick={this.onCoverPlayClick}
                        onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)} />
    );
  }

  get coversListForCarousel() {
    const { videoIds, videoByIds } = this.props;

    return _.map(videoIds, (videoId, index) => (
      <div key={videoId}
           role="presentation">
        {this.renderCoverForCarousel(videoByIds[videoId], index, styles['overlay-item-topmost'])}
      </div>
    ));
  }

  get singleItemCover() {
    return (
      <div className={styles['single-item-cover']}
           role="presentation">
        {this.renderCoverForCarousel(this.mainVideo, 0)}
      </div>
    );
  }

  get coversList() {
    const { videoIds, mainVideoId } = this.props;

    if (_.size(videoIds) === 1) {
      return this.singleItemCover;
    }

    return (
      <ReloadStorage onReload={this.processDataFromStorage}>
        <SlidableListWithLazyLoad itemHeight={this.playerSize.height}
                                  className={styles['players-list-slidable']}
                                  onCurrentSlideChanged={this.onCurrentSlideChanged}
                                  selectedVideoId={mainVideoId}>
          {this.coversListForCarousel}
        </SlidableListWithLazyLoad>
      </ReloadStorage>
    );
  }

  get isMainItemChannelCover() {
    return this.props.appSettings.numbers.mainItemType === MAIN_ITEM_TYPE_VALUES.CHANNEL_COVER;
  }

  get noVideosPlayerContent() {
    const { channelData } = this.props;
    const { width, height } = this.playerSize;

    if (!this.isMainItemChannelCover) {
      return (
        <NoVideosContent width={width}
                         height={height} />
      );
    }

    const coverUrl = channelData.customCoverUrl || channelData.coverUrl;
    return (
      <ResizedImage className={styles['overlay-only']}
                    src={coverUrl}
                    width={width}
                    height={height}>
        {this.noVideoPlayerOverlay}
      </ResizedImage>
    );
  }

  get mainUI() {
    const { isVideoPlaying, channelData, canPlayVideoInFrame } = this.props;
    const { width, height } = this.playerSize;

    if (!this.mainVideo) {
      return this.noVideosPlayerContent;
    }

    return (
      <div data-hook="main-ui"
           className={styles['new-mobile-ui-container']}
           style={{ width, height }}
           role="presentation">
        <div className={styles['real-player-placeholder']}
             role="presentation">
          {canPlayVideoInFrame &&
            (<MainWidgetMobilePlayer videoItem={this.mainVideo}
                                     channelData={channelData}
                                     width={width}
                                     height={height}
                                     preload="none"
                                     showPoster={false}
                                     paused={!isVideoPlaying}
                                     onMemberSignUp={this.openMemberOnlyPage} />)
          }
        </div>
        {this.coversList}
      </div>
    );
  }

  @autobind
  reset() {
    this.props.resetSelectedVideo();
  }

  render() {
    return (
      <section data-hook="widget-container"
               data-channel-layout="mobile"
               className={styles.container}>
        <PaymentEvents onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />
        {this.mainUI}
        <DeeplinkPopups />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/mobile/views/home/home.js